//----- Component --------------------------------------------------------/
// Developer : Daiana Curcio/Nicolas Pascucci
// Creation Date : 06/03/2024
// Version : 3
// Description : Resumen semanal de pedidos a proveedores por empresa
//------Changes ----------------------------------------------------------//
// Change Date : 04/11/2026
// Change by :
// Change description: Vista de tarjetas por proveedor y vista de tabla alternativa
//========================================================================/
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { ExportToCsv } from "export-to-csv";
import moment from "moment";
import "moment/locale/es";
import Navbar from "../Navbar";
import { UrlApi } from "../../services/apirest";
import { logEventos } from "../../services/services";
import { COLORES_PRODUCTOS } from "./coloresProductos";

const PRODUCTOS = {
  butano: { clave: "butano", corto: "B", nombre: "Butano", color: "#1976D2" },
  propano: { clave: "propano", corto: "P", nombre: "Propano", color: "#EF6C00" },
  bso: { clave: "bso", corto: "BSO", nombre: "BSO", color: "#2E7D32" },
  propelente: {
    clave: "propelente",
    corto: "PPTE",
    nombre: "Propelente",
    color: "#7B1FA2",
  },
};

const EMPRESAS = [
  {
    id: 4,
    nombre: "NaturalGas",
    color: { fondo: "#F3E5F5", borde: "#7B1FA2", encabezado: "#E1BEE7" },
    productos: [PRODUCTOS.butano, PRODUCTOS.propano],
  },
  {
    id: 1,
    nombre: "Hipergas",
    color: { fondo: "#E3F2FD", borde: "#1976D2", encabezado: "#BBDEFB" },
    productos: [
      PRODUCTOS.butano,
      PRODUCTOS.propano,
      PRODUCTOS.bso,
      PRODUCTOS.propelente,
    ],
  },
];

const TOTAL_GENERAL = {
  id: "total-general",
  nombre: "Total general",
  color: { fondo: "#FFF8E1", borde: "#F9A825", encabezado: "#FFECB3" },
  productos: Object.values(PRODUCTOS),
};

const ESTADOS = {
  pendiente: { texto: "Pendiente", color: "default" },
  curso: { texto: "En curso", color: "primary" },
  completa: { texto: "Completo", color: "success" },
  revisar: { texto: "Revisar", color: "error" },
};

const COLUMNAS_CSV = [
  "Fecha            ",
  "Empresa",
  "Proveedor",
  "Boca",
  "Cantidad de Butano",
  "Cantidad de Propano",
  "Cantidad Butano Desodorizado",
  "Cantidad de Propelente",
  "Usuario",
];

const ANCHOS_COLUMNAS = {
  proveedor: "24%",
  producto: "6%",
  dia: "8.5%",
  total: "10.5%",
};

const celdaSx = {
  py: 0.25,
  px: 0.75,
  fontSize: 12,
  textAlign: "center",
  whiteSpace: "nowrap",
};

const crearSemana = (inicio) =>
  [0, 1, 2, 3, 4, 5, 6].map((dias) => {
    const fecha = inicio.clone().add(dias, "days");

    return {
      fecha: fecha.format("YYYY-MM-DD"),
      nombre: fecha.format("ddd"),
      dia: fecha.format("DD/MM"),
    };
  });

const obtenerClave = (empresa, proveedor, fecha) =>
  `${empresa}|${proveedor}|${fecha}`;

const crearSaldo = () => ({ disponible: 0, cargado: 0, tieneDatos: false });

const sumarSaldo = (saldo, disponible, cargado) => {
  const tieneDatos =
    (disponible !== null && disponible !== undefined) ||
    (cargado !== null && cargado !== undefined);

  if (!tieneDatos) return saldo;

  return {
    disponible: saldo.disponible + (Number(disponible) || 0),
    cargado: saldo.cargado + (Number(cargado) || 0),
    tieneDatos: true,
  };
};

const tieneValor = (saldo) =>
  saldo.tieneDatos && (saldo.disponible !== 0 || saldo.cargado !== 0);

const calcularPorcentaje = (saldo) => {
  if (!saldo.cargado) return 0;

  return Math.min(Math.max((saldo.disponible / saldo.cargado) * 100, 0), 100);
};

const obtenerEstado = (productos) => {
  if (productos.some((producto) => producto.total.disponible < 0)) {
    return "revisar";
  }

  const cargado = productos.reduce(
    (total, producto) => total + producto.total.cargado,
    0
  );
  const disponible = productos.reduce(
    (total, producto) => total + producto.total.disponible,
    0
  );

  if (disponible === 0) return "completa";
  if (disponible === cargado) return "pendiente";
  return "curso";
};

function BarraProducto({ producto, saldo }) {
  const excedido = saldo.disponible < 0;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 1,
          mb: 0.25,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
          {producto.nombre}
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            color: excedido ? "error.main" : "text.primary",
          }}
        >
          {excedido ? (
            <>
              Excedido en <b>{Math.abs(saldo.disponible)}</b> (cargados{" "}
              {saldo.cargado})
            </>
          ) : (
            <>
              Faltan <b>{saldo.disponible}</b> de {saldo.cargado}
            </>
          )}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={calcularPorcentaje(saldo)}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: "grey.300",
          "& .MuiLinearProgress-bar": {
            bgcolor: producto.color,
            borderRadius: 4,
          },
        }}
      />
    </Box>
  );
}

function ResumenSemana({ totales, bloques }) {
  const proveedores = bloques.flatMap((bloque) => bloque.filas);
  const completos = proveedores.filter(
    (fila) => obtenerEstado(fila.productos) === "completa"
  ).length;
  const revisar = proveedores.filter(
    (fila) => obtenerEstado(fila.productos) === "revisar"
  ).length;
  const columnas = Math.max(Math.floor(12 / (totales.length + 1)), 3);

  return (
    <Grid container spacing={1}>
      {totales.map((producto) => {
        const saldo = producto.total;
        const excedido = saldo.disponible < 0;

        return (
          <Grid item xs={12} sm={6} md={columnas} key={producto.clave}>
            <Box
              sx={{
                height: "100%",
                bgcolor: "white",
                border: "1px solid",
                borderColor: "divider",
                borderTop: `3px solid ${producto.color}`,
                borderRadius: 1,
                p: 1.25,
              }}
            >
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {producto.nombre}: {excedido ? "excedido en" : "faltan asignar"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 24,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  color: excedido ? "error.main" : "text.primary",
                }}
              >
                {Math.abs(saldo.disponible)}
                <Box
                  component="span"
                  sx={{ fontSize: 14, fontWeight: 400, color: "text.secondary" }}
                >
                  {" "}
                  {excedido ? `(cargados ${saldo.cargado})` : `de ${saldo.cargado}`}
                </Box>
              </Typography>
              <LinearProgress
                variant="determinate"
                value={calcularPorcentaje(saldo)}
                sx={{
                  mt: 0.75,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "grey.300",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: producto.color,
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          </Grid>
        );
      })}

      <Grid item xs={12} sm={6} md={columnas}>
        <Box
          sx={{
            height: "100%",
            bgcolor: "white",
            border: "1px solid",
            borderColor: "divider",
            borderTop: "3px solid #F9A825",
            borderRadius: 1,
            p: 1.25,
          }}
        >
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            Proveedores con pedidos
          </Typography>
          <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2 }}>
            {proveedores.length}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.secondary" }}>
            {completos} {completos === 1 ? "completo" : "completos"}
            {revisar > 0 ? ` · ${revisar} para revisar` : ""}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}

function TarjetaProveedor({ fila, semana, encabezado }) {
  const [abierta, setAbierta] = useState(false);
  const estado = ESTADOS[obtenerEstado(fila.productos)];

  const encabezadoSx = {
    ...celdaSx,
    fontSize: 11,
    fontWeight: 700,
    bgcolor: encabezado,
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        bgcolor: "white",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 1.25,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
          {fila.nombre}
        </Typography>
        <Chip
          size="small"
          label={estado.texto}
          color={estado.color}
          sx={{ height: 20, fontSize: 11 }}
        />
      </Box>

      {fila.productos.map((producto) => (
        <BarraProducto
          key={producto.clave}
          producto={producto}
          saldo={producto.total}
        />
      ))}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          textAlign: "center",
          bgcolor: "grey.100",
          borderRadius: 1,
          py: 0.5,
        }}
      >
        {semana.map((dia) => {
          const cargado = fila.productos.reduce(
            (total, producto) => total + producto.dias[dia.fecha].cargado,
            0
          );

          return (
            <Box key={dia.fecha}>
              <Typography
                sx={{
                  fontSize: 10,
                  color: "text.secondary",
                  textTransform: "capitalize",
                }}
              >
                {dia.nombre}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {cargado || "-"}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Button
        size="small"
        sx={{ alignSelf: "flex-end", fontSize: 11 }}
        endIcon={abierta ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        onClick={() => setAbierta((valor) => !valor)}
      >
        {abierta ? "Ocultar detalle" : "Ver detalle"}
      </Button>

      <Collapse in={abierta} unmountOnExit>
        <Table size="small" sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...encabezadoSx, textAlign: "left", width: "28%" }}>
                Día
              </TableCell>
              {fila.productos.map((producto) => (
                <TableCell key={producto.clave} sx={encabezadoSx}>
                  {producto.corto}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {semana.map((dia) => (
              <TableRow key={dia.fecha}>
                <TableCell
                  sx={{
                    ...celdaSx,
                    textAlign: "left",
                    textTransform: "capitalize",
                  }}
                >
                  {dia.nombre} {dia.dia}
                </TableCell>
                {fila.productos.map((producto) => {
                  const saldo = producto.dias[dia.fecha];

                  return (
                    <TableCell
                      key={producto.clave}
                      sx={{
                        ...celdaSx,
                        bgcolor: COLORES_PRODUCTOS[producto.clave].fila,
                        color: saldo.disponible < 0 ? "error.main" : "inherit",
                      }}
                    >
                      {tieneValor(saldo)
                        ? `${saldo.disponible} / ${saldo.cargado}`
                        : "-"}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Collapse>
    </Box>
  );
}

function CeldaSaldo({ saldo, sx }) {
  if (!tieneValor(saldo)) {
    return (
      <TableCell sx={{ ...celdaSx, color: "text.disabled", ...sx }}>-</TableCell>
    );
  }

  return (
    <TableCell
      sx={{
        ...celdaSx,
        color: saldo.disponible < 0 ? "error.main" : "inherit",
        ...sx,
      }}
    >
      {saldo.disponible} / {saldo.cargado}
    </TableCell>
  );
}

function TablaEmpresa({
  empresa,
  semana,
  filas,
  totales,
  etiquetaTotal = "TOTAL",
}) {
  const { color } = empresa;

  const encabezadoSx = {
    ...celdaSx,
    fontSize: 11,
    fontWeight: 700,
    bgcolor: color.encabezado,
  };

  return (
    <TableContainer sx={{ bgcolor: "white" }}>
      <Table size="small" sx={{ minWidth: 780, tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: ANCHOS_COLUMNAS.proveedor }} />
          <col style={{ width: ANCHOS_COLUMNAS.producto }} />
          {semana.map((dia) => (
            <col key={dia.fecha} style={{ width: ANCHOS_COLUMNAS.dia }} />
          ))}
          <col style={{ width: ANCHOS_COLUMNAS.total }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...encabezadoSx, textAlign: "left" }}>
              Proveedor
            </TableCell>
            <TableCell sx={encabezadoSx}>Prod.</TableCell>
            {semana.map((dia) => (
              <TableCell
                key={dia.fecha}
                sx={{ ...encabezadoSx, textTransform: "capitalize" }}
              >
                {dia.nombre} {dia.dia}
              </TableCell>
            ))}
            <TableCell
              sx={{
                ...encabezadoSx,
                borderLeft: "2px solid",
                borderLeftColor: "divider",
              }}
            >
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filas.map((fila) =>
            fila.productos.map((producto, index) => {
              const colorFila = COLORES_PRODUCTOS[producto.clave].fila;

              return (
                <TableRow key={`${fila.id}-${producto.clave}`}>
                  {index === 0 ? (
                    <TableCell
                      rowSpan={fila.productos.length}
                      sx={{
                        ...celdaSx,
                        textAlign: "left",
                        fontWeight: 500,
                        verticalAlign: "top",
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        bgcolor: "white",
                      }}
                    >
                      {fila.nombre}
                    </TableCell>
                  ) : null}
                  <TableCell
                    sx={{ ...celdaSx, fontWeight: 700, bgcolor: colorFila }}
                  >
                    {producto.corto}
                  </TableCell>
                  {semana.map((dia) => (
                    <CeldaSaldo
                      key={dia.fecha}
                      saldo={producto.dias[dia.fecha]}
                      sx={{ bgcolor: colorFila }}
                    />
                  ))}
                  <CeldaSaldo
                    saldo={producto.total}
                    sx={{
                      fontWeight: 700,
                      bgcolor: colorFila,
                      borderLeft: "2px solid",
                      borderLeftColor: "divider",
                    }}
                  />
                </TableRow>
              );
            })
          )}

          {totales.map((producto, index) => {
            const colorTotal = COLORES_PRODUCTOS[producto.clave].total;

            return (
              <TableRow key={`total-${producto.clave}`}>
                {index === 0 ? (
                  <TableCell
                    rowSpan={totales.length}
                    sx={{
                      ...celdaSx,
                      textAlign: "left",
                      fontWeight: 700,
                      bgcolor: color.fondo,
                      verticalAlign: "top",
                    }}
                  >
                    {etiquetaTotal}
                  </TableCell>
                ) : null}
                <TableCell
                  sx={{ ...celdaSx, fontWeight: 700, bgcolor: colorTotal }}
                >
                  {producto.corto}
                </TableCell>
                {semana.map((dia) => (
                  <CeldaSaldo
                    key={dia.fecha}
                    saldo={producto.dias[dia.fecha]}
                    sx={{ fontWeight: 700, bgcolor: colorTotal }}
                  />
                ))}
                <CeldaSaldo
                  saldo={producto.total}
                  sx={{
                    fontWeight: 700,
                    bgcolor: colorTotal,
                    borderLeft: "2px solid",
                    borderLeftColor: "divider",
                  }}
                />
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function BloqueEmpresa({ empresa, semana, filas, totales, vista }) {
  const { color } = empresa;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderTop: `3px solid ${color.borde}`,
        borderRadius: 1,
        bgcolor: color.fondo,
        p: 1,
      }}
    >
      <Typography sx={{ mb: 0.75, fontSize: 13, fontWeight: 700 }}>
        {empresa.nombre}
        {vista === "tabla" ? (
          <Box
            component="span"
            sx={{ ml: 0.5, fontWeight: 400, color: "text.secondary" }}
          >
            (disponibles / cargados)
          </Box>
        ) : null}
      </Typography>

      {filas.length === 0 ? (
        <Alert severity="info">
          No hay pedidos de {empresa.nombre} en esta semana.
        </Alert>
      ) : vista === "tarjetas" ? (
        <Grid container spacing={1} alignItems="stretch">
          {filas.map((fila) => (
            <Grid item xs={12} sm={6} lg={4} xl={3} key={fila.id}>
              <TarjetaProveedor
                fila={fila}
                semana={semana}
                encabezado={color.encabezado}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <TablaEmpresa
          empresa={empresa}
          semana={semana}
          filas={filas}
          totales={totales}
        />
      )}
    </Box>
  );
}

function Proveedores_abastecimiento() {
  const [inicioSemana, setInicioSemana] = useState(() => moment().day(8));
  const [vista, setVista] = useState("tarjetas");
  const [bocas, setBocas] = useState([]);
  const [abastecimiento, setAbastecimiento] = useState([]);
  const [datosCsv, setDatosCsv] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  const inicioTexto = inicioSemana.format("YYYY-MM-DD");
  const semana = useMemo(() => crearSemana(inicioSemana), [inicioTexto]);
  const periodoTexto =
    inicioSemana.format("DD/MM") +
    " al " +
    inicioSemana.clone().add(6, "days").format("DD/MM/YYYY");

  useEffect(() => {
    logEventos(
      "Acceso al menú resumen de petroleras",
      "Resumen de Petroleras",
      "Ver resumen de pedidos de NaturalGas y de Hipergas",
      localStorage.getItem("user")
    );
  }, []);

  useEffect(() => {
    let vigente = true;

    const desde = semana[0].fecha;
    const hasta = semana[semana.length - 1].fecha;

    const pedirLista = async (ruta) => {
      const res = await fetch(UrlApi + ruta);
      const data = await res.json().catch(() => null);

      if (!res.ok || !Array.isArray(data)) {
        throw new Error("Respuesta inválida en " + ruta);
      }

      return data;
    };

    setCargando(true);
    setError("");

    Promise.all([
      pedirLista("/bocas_carga"),
      pedirLista(`/proveedores_abastecimiento/acotado/${desde}/${hasta}`),
      pedirLista(`/proveedores_abastecimiento/csv/${desde}/${hasta}`),
    ])
      .then(([listaBocas, listaAbastecimiento, listaCsv]) => {
        if (!vigente) return;

        setBocas(listaBocas);
        setAbastecimiento(listaAbastecimiento);
        setDatosCsv(listaCsv);
      })
      .catch((err) => {
        console.error(err);
        if (vigente) {
          setBocas([]);
          setAbastecimiento([]);
          setDatosCsv([]);
          setError(
            "No se pudo cargar el resumen. Recargá la pantalla o avisá a Sistemas."
          );
        }
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });

    return () => {
      vigente = false;
    };
  }, [semana]);

  const bloques = useMemo(() => {
    const registros = new Map();

    abastecimiento.forEach((dato) => {
      const clave = obtenerClave(
        dato.empresa_destino,
        dato.proveedor,
        moment(dato.fecha).format("YYYY-MM-DD")
      );
      const lista = registros.get(clave) || [];
      lista.push(dato);
      registros.set(clave, lista);
    });

    return EMPRESAS.map((empresa) => {
      const filas = bocas
        .map((boca) => {
          const productos = empresa.productos
            .map((producto) => {
              const dias = {};
              let total = crearSaldo();

              semana.forEach((dia) => {
                let saldo = crearSaldo();

                (
                  registros.get(
                    obtenerClave(empresa.id, boca.idbocas_carga, dia.fecha)
                  ) || []
                ).forEach((dato) => {
                  saldo = sumarSaldo(
                    saldo,
                    dato[`viaje_${producto.clave}`],
                    dato[producto.clave]
                  );
                });

                dias[dia.fecha] = saldo;

                if (tieneValor(saldo)) {
                  total = sumarSaldo(total, saldo.disponible, saldo.cargado);
                }
              });

              return { ...producto, dias, total };
            })
            .filter((producto) => tieneValor(producto.total));

          return {
            id: boca.idbocas_carga,
            nombre: boca.proveedor + " - " + boca.nombre_boca,
            productos,
          };
        })
        .filter((fila) => fila.productos.length > 0);

      const totales = empresa.productos
        .map((producto) => {
          const dias = {};
          let total = crearSaldo();

          semana.forEach((dia) => {
            let saldo = crearSaldo();

            filas.forEach((fila) => {
              const productoFila = fila.productos.find(
                (item) => item.clave === producto.clave
              );
              const saldoDia = productoFila?.dias[dia.fecha];

              if (saldoDia && tieneValor(saldoDia)) {
                saldo = sumarSaldo(saldo, saldoDia.disponible, saldoDia.cargado);
              }
            });

            dias[dia.fecha] = saldo;

            if (tieneValor(saldo)) {
              total = sumarSaldo(total, saldo.disponible, saldo.cargado);
            }
          });

          return { ...producto, dias, total };
        })
        .filter((producto) => tieneValor(producto.total));

      return { empresa, filas, totales };
    });
  }, [abastecimiento, bocas, semana]);

  const totalesGenerales = useMemo(
    () =>
      TOTAL_GENERAL.productos
        .map((producto) => {
          const dias = {};
          let total = crearSaldo();

          semana.forEach((dia) => {
            let saldo = crearSaldo();

            bloques.forEach((bloque) => {
              const totalEmpresa = bloque.totales.find(
                (item) => item.clave === producto.clave
              );
              const saldoDia = totalEmpresa?.dias[dia.fecha];

              if (saldoDia && tieneValor(saldoDia)) {
                saldo = sumarSaldo(saldo, saldoDia.disponible, saldoDia.cargado);
              }
            });

            dias[dia.fecha] = saldo;

            if (tieneValor(saldo)) {
              total = sumarSaldo(total, saldo.disponible, saldo.cargado);
            }
          });

          return { ...producto, dias, total };
        })
        .filter((producto) => tieneValor(producto.total)),
    [bloques, semana]
  );

  const hayDatos = bloques.some((bloque) => bloque.filas.length > 0);

  const exportarCsv = () => {
    const exportador = new ExportToCsv({
      fieldSeparator: ",",
      quoteStrings: '"',
      decimalSeparator: ".",
      showLabels: true,
      useBom: true,
      useKeysAsHeaders: false,
      filename: "Lista de Proveedores de Abastecimiento",
      headers: COLUMNAS_CSV,
    });

    exportador.generateCsv(datosCsv);
  };

  return (
    <>
      <Navbar />

      <Box
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          bgcolor: "grey.50",
          minHeight: "calc(100vh - 64px)",
        }}
      >
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderTop: "3px solid #00796B",
            borderRadius: 1,
            bgcolor: "#E0F2F1",
            px: 1.5,
            py: 0.75,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            columnGap: 2,
            rowGap: 0.5,
          }}
        >
          <Typography sx={{ fontWeight: 700 }}>Resumen de proveedores</Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Semana anterior">
              <IconButton
                size="small"
                onClick={() =>
                  setInicioSemana((fecha) => fecha.clone().subtract(7, "days"))
                }
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2">
              Semana: <b>{periodoTexto}</b>
            </Typography>
            <Tooltip title="Semana siguiente">
              <IconButton
                size="small"
                onClick={() =>
                  setInicioSemana((fecha) => fecha.clone().add(7, "days"))
                }
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <ToggleButtonGroup
            exclusive
            size="small"
            value={vista}
            sx={{ bgcolor: "white" }}
            onChange={(event, valor) => {
              if (valor) setVista(valor);
            }}
          >
            <ToggleButton value="tarjetas" sx={{ py: 0.25, fontSize: 11 }}>
              <ViewModuleIcon sx={{ mr: 0.5, fontSize: 16 }} />
              Tarjetas
            </ToggleButton>
            <ToggleButton value="tabla" sx={{ py: 0.25, fontSize: 11 }}>
              <TableRowsIcon sx={{ mr: 0.5, fontSize: 16 }} />
              Tabla
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            size="small"
            variant="outlined"
            sx={{ bgcolor: "white" }}
            endIcon={<FileDownloadIcon />}
            disabled={cargando || datosCsv.length === 0}
            onClick={exportarCsv}
          >
            Exportar CSV
          </Button>
        </Box>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {cargando ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : null}

        {!cargando && !error && !hayDatos ? (
          <Alert severity="info">No hay pedidos en esta semana.</Alert>
        ) : null}

        {!cargando && !error && hayDatos ? (
          <>
            <ResumenSemana totales={totalesGenerales} bloques={bloques} />

            {bloques.map((bloque) => (
              <BloqueEmpresa
                key={bloque.empresa.id}
                empresa={bloque.empresa}
                semana={semana}
                filas={bloque.filas}
                totales={bloque.totales}
                vista={vista}
              />
            ))}

            {vista === "tabla" ? (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderTop: `3px solid ${TOTAL_GENERAL.color.borde}`,
                  borderRadius: 1,
                  bgcolor: TOTAL_GENERAL.color.fondo,
                  p: 1,
                }}
              >
                <Typography sx={{ mb: 0.75, fontSize: 13, fontWeight: 700 }}>
                  {TOTAL_GENERAL.nombre}
                  <Box
                    component="span"
                    sx={{ ml: 0.5, fontWeight: 400, color: "text.secondary" }}
                  >
                    (disponibles / cargados)
                  </Box>
                </Typography>
                <TablaEmpresa
                  empresa={TOTAL_GENERAL}
                  semana={semana}
                  filas={[]}
                  totales={totalesGenerales}
                  etiquetaTotal="TODAS LAS EMPRESAS"
                />
              </Box>
            ) : null}
          </>
        ) : null}
      </Box>
    </>
  );
}

export default Proveedores_abastecimiento;